// 조용한 온천 규칙과 NPC: 탕 자리 잡기·대기·나오기, 온천용 이동(step), NPC 판단(think), 29마리 배치.
// 프로토타입 randomLand~startExit 1655–1698, step 1806–1884, think 1897–1937, 배치 2114–2136.
import * as THREE from 'three';
import type { NavGrid } from '../../engine/nav';
import { angDiff, rand, rnd } from '../../engine/math';
import { makeAgent, setSize, type Capy, type EmoteKey } from '../../capy/agent';
import { moveTo } from '../../capy/walker';
import { climbStep, hopDown, startClimb, type LandingFn } from '../../capy/stacking';
import { canClimb } from '../../capy/stackRules';
import { setFace, setHead } from '../../capy/equip';
import type { FurTone } from '../../capy/model';
import type { HeadItemId } from '@capy/shared';
import { FRONT, inPool, type Pool, type QuietLayout, type Slot, type WaitSpot } from './layout';

/** 온천에서 카피바라가 갖는 상태. 프로토타입처럼 카피바라 객체에 직접 둔다. */
export interface QuietCapy extends Capy {
  mode: 'idle' | 'wander' | 'stroll' | 'goto' | 'bathe' | 'wait' | 'exit' | 'climb' | 'riding' | 'hopping' | 'indoors';
  slot: Slot | null;
  pool: Pool | null;
  /** 0: 탕 틈으로 다가가는 중, 1: 탕 안 자리로 가는 중. */
  stage: number;
  gap: number;
  settled: boolean;
  stayUntil: number;
  best: number;
  bestT: number;
  wait: WaitSpot | null;
  waitPool: Pool | null;
  exitPt: THREE.Vector3;
  goalV: THREE.Vector3;
  then: 'idle' | 'wander';
  wander: THREE.Vector3 | null;
  next: number;
  rideUntil?: number;
  lastGoal?: unknown;
  /** 문에 도착하면 료칸 식당으로 들어간다. */
  enterAfter?: boolean;
  /** 식당에서 막 나왔으면 문에서 3m 멀어질 때까지 다시 들어가지 않는다. */
  doorGuard?: boolean;
}

export interface QuietRules {
  L: QuietLayout;
  nav: NavGrid;
  agents: QuietCapy[];
  /** 탕에 잠긴 카피바라 가까이에서 내릴 때 탕 출입구로 내려서게 한다. */
  landing: LandingFn;
}

export const poolAt = (L: QuietLayout, p: { x: number; z: number }): Pool | null => L.pools.find((P) => inPool(P, p.x, p.z)) || null;
const gapPoint = (P: Pool, g: number, v: THREE.Vector3) => { const R = P.rAt(g) + 0.45; return v.set(P.x + Math.cos(g) * R, 0, P.z + Math.sin(g) * R); };
const nearestGap = (P: Pool, ang: number) => P.gaps.reduce((b, g) => Math.abs(angDiff(g, ang)) < Math.abs(angDiff(b, ang)) ? g : b, P.gaps[0]!);
const exitGap = (a: QuietCapy, P: Pool) => gapPoint(P, nearestGap(P, Math.atan2(a.pos.z - P.z, a.pos.x - P.x)), a.goalV);

export function randomLand(R: QuietRules, near?: { x: number; z: number }): THREE.Vector3 {
  for (let i = 0; i < 40; i++) {
    let x = rand(-14, 14), z = rand(-9, 11);
    if (near && i < 30) { x = Math.max(-14, Math.min(14, near.x + rand(-5, 5))); z = Math.max(-9, Math.min(11, near.z + rand(-5, 5))); }
    if (R.L.pools.every((P) => !inPool(P, x, z, 1.5)) && !R.nav.blockedAt(x, z)) return new THREE.Vector3(x, 0, z);
  }
  return new THREE.Vector3(0, 0, 5.5);
}

export function releaseSlot(a: QuietCapy): void { if (a.slot) { a.slot.occ = null; a.slot = null; } }
export function releaseWait(a: QuietCapy): void { if (a.wait) { a.wait.occ = null; a.wait = null; } a.seated = false; }

function nearestFree<S extends { x: number; z: number; occ: unknown }>(list: S[], a: Capy): S | null {
  let best: S | null = null, bd = 1e9;
  list.forEach((s) => { if (s.occ) return; const d = Math.hypot(a.pos.x - s.x, a.pos.z - s.z); if (d < bd) { bd = d; best = s; } });
  return best;
}

/** 숨은 탕이 차 있으면 길가 벤치에서 기다린다. */
export function startWait(R: QuietRules, a: QuietCapy, P: Pool, t: number): boolean {
  if (!P.wait) return false;
  releaseSlot(a); releaseWait(a);
  const w = nearestFree(P.wait, a);
  if (!w) return false;
  w.occ = a; w.since = t; a.wait = w; a.waitPool = P; a.mode = 'wait'; a.settled = false; a.napping = false;
  return true;
}

/** 빈 자리를 잡아 탕으로 간다. only가 없으면 내 카피바라는 모든 탕, NPC는 광장 탕(없으면 큰 탕 가장자리). */
export function startBath(R: QuietRules, a: QuietCapy, t: number, only?: Slot[]): boolean {
  releaseSlot(a); releaseWait(a);
  const all = R.L.pools.flatMap((P) => P.slots), plaza = R.L.pools.filter((P) => P.name !== 'hidden').flatMap((P) => P.slots);
  const s = only ? nearestFree(only, a) : a.isPlayer ? (nearestFree(all, a) || nearestFree(R.L.seats, a)) : (nearestFree(plaza, a) || nearestFree(R.L.seats, a));
  if (!s) return false;
  s.occ = a; a.slot = s; a.pool = s.pool; a.settled = false; a.napping = false; a.path = null; a.best = 1e9; a.bestT = t;
  a.gap = s.seat ? s.ang : nearestGap(s.pool, s.ang || FRONT);
  a.mode = 'bathe'; a.stage = (!s.seat && poolAt(R.L, a.pos) === s.pool) ? 1 : 0;
  return true;
}

/** 탕에서 나온다 (탕 안이면 가장 가까운 틈으로). NPC는 나온 뒤 어슬렁거린다. */
export function startExit(R: QuietRules, a: QuietCapy): void {
  const inside = poolAt(R.L, a.pos);
  releaseSlot(a); a.settled = false; a.napping = false;
  a.then = a.isPlayer ? 'idle' : 'wander';
  if (inside) { const ang = Math.atan2(a.pos.z - inside.z, a.pos.x - inside.x); gapPoint(inside, nearestGap(inside, ang), a.exitPt); a.pool = inside; a.mode = 'exit'; }
  else if (a.then === 'wander') { a.mode = 'wander'; a.wander = randomLand(R); }
  else a.mode = 'idle';
}

/** 물에 잠긴 카피바라는 비켜 가지 않는다 (온천 규칙). */
const ignoreBathing = (o: Capy) => o.bathAmt > 0.5;

/** 온천에서 한 프레임 이동. 움직였으면 true. 프로토타입 step 1806–1884. */
export function quietStep(R: QuietRules, a: QuietCapy, t: number, dt: number): boolean {
  const L = R.L;
  if (a.riding || a.hop || a.mode === 'riding' || a.mode === 'hopping') { a.bathAmt = 0; return false; }
  const water = () => { const nowIn = poolAt(L, a.pos); return nowIn && inPool(nowIn, a.pos.x, a.pos.z, -0.3) ? 1 : 0; };
  if (a.mode === 'climb') {
    return climbStep(a, t, dt, R.nav, R.agents, { ignore: ignoreBathing, speedK: 1 - 0.3 * a.bathAmt, wet: water });
  }
  if (a.mode === 'wait' && a.wait) {
    const P = a.waitPool!;
    if (!P.slots[0]!.occ) {
      const first = P.wait!.filter((w) => w.occ).sort((x, y) => x.since - y.since)[0];
      if (first === a.wait) startBath(R, a, t, P.slots);
    }
  }
  const inside = poolAt(L, a.pos);
  let goal: { x: number; z: number } | null = null, direct = false;
  if (a.mode === 'wait' && a.wait) {
    if (inside) { goal = exitGap(a, inside); direct = true; } else goal = a.wait;
  } else if (a.mode === 'wander' || a.mode === 'stroll' || a.mode === 'goto') {
    if (inside) { goal = exitGap(a, inside); direct = true; }
    else { if (!a.wander) a.wander = randomLand(R); goal = a.wander; }
  } else if (a.mode === 'bathe') {
    const P = a.pool!, s = a.slot!;
    if (s.seat) goal = s;
    else {
      if (a.stage === 0 && inPool(P, a.pos.x, a.pos.z)) a.stage = 1;
      if (a.stage === 0) goal = gapPoint(P, a.gap, a.goalV);
      else { goal = s; direct = true; }
    }
  } else if (a.mode === 'exit') {
    goal = a.exitPt; direct = true;
  }
  if (goal !== a.lastGoal) { a.lastGoal = goal; a.lastDg = null; }
  let moving = false;
  if (goal) {
    moving = moveTo(a, goal, dt, t, R.nav, R.agents, direct, ignoreBathing, 1 - 0.3 * a.bathAmt);
    if (moving) a.seated = false;
    else {
      if (a.mode === 'wait') { if (goal === a.wait) { a.targetFacing = a.wait.face; a.seated = true; } }
      else if (a.mode === 'bathe' && !a.slot!.seat && a.stage === 0) a.stage = 1;
      else if (a.mode === 'bathe') {
        a.targetFacing = a.slot!.seat ? Math.atan2(a.pool!.x - a.pos.x, a.pool!.z - a.pos.z) : a.slot!.face;
        if (!a.settled) { a.settled = true; a.stayUntil = t + rand(80, 180); }
      }
      else if (a.mode === 'exit') { if (a.then === 'wander') { a.mode = 'wander'; a.wander = randomLand(R, a.pos); } else a.mode = 'idle'; }
      else if (a.mode === 'wander') { a.mode = 'idle'; a.wander = null; }
      else if (a.mode === 'stroll') a.wander = randomLand(R);
      else if (a.mode === 'goto') { a.mode = 'idle'; a.wander = null; }
    }
  }
  if (a.mode === 'bathe' && !a.settled && goal && !a.isPlayer) {
    const dFinal = Math.hypot(a.pos.x - goal.x, a.pos.z - goal.z);
    if (dFinal < a.best - 0.25) { a.best = dFinal; a.bestT = t; }
    else if (t - a.bestT > 25) { releaseSlot(a); a.mode = 'wander'; a.wander = randomLand(R); }
  }
  const nowIn = poolAt(L, a.pos);
  if (!moving && !nowIn && !(a.slot && a.slot.seat && a.settled) && a.mode !== 'wait') {
    let sx = 0, sz = 0;
    R.agents.forEach((o) => {
      if (o === a || o.bathAmt > 0.5 || o.riding || o.hop) return;
      const ox = a.pos.x - o.pos.x, oz = a.pos.z - o.pos.z, od = Math.hypot(ox, oz);
      if (od < 1.6 && od > 0.001) { const w = (1.6 - od) / 1.6; sx += ox / od * w; sz += oz / od * w; }
    });
    if (Math.hypot(sx, sz) > 0.05) { const nx = a.pos.x + sx * dt * 0.35, nz = a.pos.z + sz * dt * 0.35; if (!R.nav.blockedAt(nx, nz)) { a.pos.x = nx; a.pos.z = nz; } }
  }
  const inWater = nowIn && inPool(nowIn, a.pos.x, a.pos.z, -0.3) ? 1 : 0;
  a.bathAmt += (inWater - a.bathAmt) * Math.min(1, dt * 0.9);
  return moving;
}

const EMOTE_KEYS: EmoteKey[] = ['wave', 'wobble', 'waddle', 'yawn'];

/** NPC가 몇 초마다 다음 할 일을 정한다. 프로토타입 think 1897–1937 (식당 손님은 ryokan.ts). */
export function think(R: QuietRules, a: QuietCapy, t: number): void {
  if (a.mode === 'indoors' || a.isPlayer || t < a.next) return;
  const wanderFrom = (x: Capy) => { const q = x as QuietCapy; q.mode = 'wander'; q.wander = randomLand(R, q.pos); };
  if (a.mode === 'riding' || a.mode === 'hopping') {
    if (a.mode === 'riding' && a.riding && t > (a.rideUntil || 0)) hopDown(a, t, R.nav, wanderFrom, R.landing);
    a.next = t + 2; return;
  }
  if (a.mode === 'climb') { a.next = t + 3; return; }
  if (a.mode === 'wait') {
    if (a.wait && t - a.wait.since > 120) { releaseWait(a); a.mode = 'wander'; a.wander = randomLand(R); }
    a.next = t + 5; return;
  }
  if (a.mode === 'bathe') {
    if (a.settled && t > a.stayUntil) { startExit(R, a); a.next = t + rand(4, 10); return; }
    if (a.settled && Math.random() < 0.35) {
      const r = Math.random();
      if (r < 0.35) a.napping = !a.napping;
      else if (!a.napping) { a.emote = r < 0.7 ? 'yawn' : 'wobble'; a.emoteStart = t; }
    }
    a.next = t + rand(6, 16); return;
  }
  if (a.mode === 'idle') {
    if (Math.random() < 0.08) {
      const near = R.agents.filter((o) => o !== a && !o.hop && Math.hypot(o.pos.x - a.pos.x, o.pos.z - a.pos.z) < 7 && canClimb(a, o));
      if (near.length && startClimb(a, near[Math.floor(Math.random() * near.length)]!)) { a.rideUntil = t + rand(20, 45); a.next = t + 4; return; }
    }
    const r = Math.random();
    if (r < 0.8) {
      const hidden = R.L.pools.filter((P) => P.name === 'hidden');
      const HP = Math.random() < 0.08 ? hidden[Math.floor(Math.random() * 2)]! : null;
      if (!(HP && (startBath(R, a, t, HP.slots) || (Math.random() < 0.5 && startWait(R, a, HP, t)))) && !startBath(R, a, t)) { a.mode = 'wander'; a.wander = randomLand(R, a.pos); }
    }
    else if (r < 0.94) { a.mode = 'wander'; a.wander = randomLand(R); }
    else { a.emote = EMOTE_KEYS[Math.floor(Math.random() * 4)]!; a.emoteStart = t; }
    a.next = t + rand(3, 8); return;
  }
  a.next = t + 3;
}

/** 카피바라에 온천 상태를 붙인다. */
export function asQuiet(a: Capy): QuietCapy {
  return Object.assign(a, {
    mode: 'idle', slot: null, pool: null, stage: 0, gap: FRONT, settled: false, stayUntil: 0, best: 1e9, bestT: 0,
    wait: null, waitPool: null, exitPt: new THREE.Vector3(), goalV: new THREE.Vector3(), then: 'idle', wander: null, next: 0,
  } as const) as unknown as QuietCapy;
}

/** 탕 안에서 내릴 때는 탕 출입구로. 프로토타입 landingSpot 1763–1768의 온천 부분. */
export function quietLanding(L: QuietLayout): LandingFn {
  return (bot) => { const inside = poolAt(L, bot.pos); return inside ? gapPoint(inside, nearestGap(inside, Math.atan2(bot.pos.z - inside.z, bot.pos.x - inside.x)), new THREE.Vector3()) : null; };
}

/** 내 카피바라(오렌지를 얹고 탕 앞)와 NPC 29마리 배치. 17마리는 광장 탕에 앉아 있고 나머지는 땅에. */
export function populate(R: QuietRules, world: THREE.Object3D): QuietCapy {
  const player = asQuiet(makeAgent(world, true, 'classic'));
  setHead(player, 'orange');
  player.pos.set(2.8, 0, 4.4); player.facing = player.targetFacing = 0;
  R.agents.push(player);
  const tones: FurTone[] = ['classic', 'honey', 'cocoa'];
  const itemPool: (HeadItemId | 'none')[] = ['none', 'none', 'orange', 'yuzu', 'yuzu', 'duckling', 'frog', 'orange', 'towel', 'towel'];
  const plaza = R.L.pools.filter((P) => P.name !== 'hidden').flatMap((P) => P.slots);
  for (let i = 0; i < 29; i++) {
    const a = asQuiet(makeAgent(world, false, tones[i % 3]!));
    setHead(a, itemPool[Math.floor(rnd(i + 500) * itemPool.length)]!);
    if (i % 9 === 4) setSize(a, 0.5);
    if (rnd(i + 600) < 0.22) setFace(a, 'bubble');
    R.agents.push(a);
    if (i < 17) {
      const free = plaza.filter((x) => !x.occ), s = free[Math.floor(rnd(i + 700) * free.length)]!;
      s.occ = a; a.slot = s; a.pool = s.pool; a.mode = 'bathe'; a.stage = 1; a.settled = true;
      a.pos.set(s.x, 0, s.z); a.bathAmt = 1; a.facing = a.targetFacing = s.face;
      a.stayUntil = rand(15, 140); a.next = rand(2, 12);
    } else {
      a.pos.copy(randomLand(R)); a.facing = a.targetFacing = rand(-Math.PI, Math.PI); a.next = rand(1, 10);
    }
  }
  return player;
}

/**
 * 내 카피바라가 바닥을 탭했을 때 (프로토타입 onTapAt 3941–3958 중 온천 부분, 문은 ryokan).
 * 탕을 탭하면 빈 자리로 들어가고, 숨은 탕이 차 있으면 대기 벤치로, 그 밖은 그 자리로 걸어간다.
 */
export function quietTap(R: QuietRules, a: QuietCapy, x: number, z: number, t: number): void {
  if (a.mode === 'climb') { a.mode = 'idle'; a.climbT = null; }
  const P = R.L.pools.find((Q) => inPool(Q, x, z, 0.3));
  a.napping = false; a.emote = null;
  if (P) {
    if (!(a.mode === 'bathe' && a.pool === P) && !startBath(R, a, t, P.slots) && !(P.name === 'hidden' && startWait(R, a, P, t))) {
      releaseSlot(a); a.settled = false; a.mode = 'goto';
      a.wander = gapPoint(P, nearestGap(P, Math.atan2(a.pos.z - P.z, a.pos.x - P.x)), new THREE.Vector3());
    }
  } else {
    releaseSlot(a); releaseWait(a); a.settled = false; a.mode = 'goto'; a.wander = new THREE.Vector3(x, 0, z);
  }
}
